import type { ReactNode } from "react";

import { PermisosSection } from "./sections/controlDeAcceso";
import {
  PlumasSection,
  VehiculosSection,
  LecturaPlacasSection,
  RegistroVehicularSection,
} from "./sections/accesoVehicular";
import { PlantillasSection, AutorizacionesSection } from "./sections/accesosTemporales";
import {
  ReglasYAuditoriaDashboard,
  ReglasSection as ReglasAccesoSection,
  ListasNegrasSection,
  AntiPassbackSection,
  AuditoriaSection,
  SospechososSection,
} from "./sections/reglasYAuditoria";
import { ReportesCampoSection, ChatDirectoSection } from "./sections/seguridadVigilancia";
import {
  ComunidadDashboard,
  DirectorioSection,
  UnidadesSection,
  RolesSection,
  GuardiasSection,
  MembresiasSection,
  SuscripcionesSection,
} from "./sections/gestionComunidad";
import {
  ComunicacionDashboard,
  AnunciosSection,
  NotificacionesSection,
  SegmentosSection,
  CanalesSection,
} from "./sections/comunicacion";
import {
  PagosDashboard,
  CuotasSection,
  PagosSection,
  ProductosSection,
  ReportesIngresosDashboard,
} from "./sections/pagos";
import {
  ReservasDashboard,
  AmenidadesSection,
  ReservacionesSection,
  ReglasSection,
  RecordatoriosSection,
} from "./sections/reservas";
import { LectoresSection, MantenimientoSection } from "./sections/dispositivos";
// Pantallas tipo iVMS-4200 que leen y escriben por el API (/access-control/*, CC-CONSOLE #1593).
import AccessDashboard from "./screens/AccessDashboard";
import AccessPointsConsole from "./screens/AccessPointsConsole";
import EventsLog from "./screens/EventsLog";
import PassesConsole from "./screens/PassesConsole";
import PassesDashboard from "./screens/PassesDashboard";
import SecurityDashboard from "./screens/SecurityDashboard";
import CameraWall from "./screens/CameraWall";
import { CAMERA_ICON } from "./components/console/Console";
import AlertsConsole from "./screens/AlertsConsole";
import RondinesLink from "./screens/RondinesLink";
import DevicesConsole from "./screens/DevicesConsole";
import VehicleAccessDashboard from "./screens/VehicleAccessDashboard";
// Control de estacionamientos (CC-CONSOLE #1594): API /parking/*.
import ParkingDashboard from "./parking/ParkingDashboard";
import OccupancyScreen from "./parking/OccupancyScreen";
import TicketsScreen from "./parking/TicketsScreen";
import CashierScreen from "./parking/CashierScreen";
import RatesScreen from "./parking/RatesScreen";
import PensionsScreen from "./parking/PensionsScreen";
import InvoicingScreen from "./parking/InvoicingScreen";
import ShiftsScreen from "./parking/ShiftsScreen";
import SettingsScreen from "./parking/SettingsScreen";

// ---------------------------------------------------------------------------
// Single source of truth for the Nuvex Access navigation tree. Drives:
//   - the apps-sidebar category/apps (appsConfig.tsx)
//   - the per-functionality sub-menu (NuvexAccessLayout)
//   - the nested routes (router/config.tsx via routes.tsx)
// The CRUD *content* lives in each bespoke section component; this file only
// wires labels / icons / paths / elements together.
// ---------------------------------------------------------------------------

export interface NavItem {
  id: string; // route segment under the functionality
  label: string;
  icon: string; // remixicon class for the sub-menu
  element: ReactNode;
}

/**
 * Apps-sidebar category a functionality belongs to. The legacy single
 * "Nuvex Access" tile group is split into two top-level categories:
 *   - "access-control" — control de acceso, seguridad y dispositivos
 *   - "comunity"       — gestión de comunidad, comunicación y pagos
 * (see appsConfig.tsx, which builds one AppsSidebar category per value).
 */
export type NuvexCategory = "access-control" | "comunity";

export interface Functionality {
  slug: string; // route base: /nuvex-access/<slug>
  name: string;
  description: string;
  category: NuvexCategory; // apps-sidebar category grouping
  iconClass: string; // apps-sidebar icon
  color: string; // tailwind bg color for the app tile
  remixIcon: string; // sub-menu / header icon
  menu: NavItem[];
}

export const FUNCTIONALITIES: Functionality[] = [
  {
    slug: "control-de-acceso",
    name: "Control de acceso",
    description: "Puertas, torniquetes, permisos e invitaciones",
    category: "access-control",
    iconClass: "ri-lock-line",
    color: "bg-blue-500",
    remixIcon: "ri-door-lock-line",
    menu: [
      { id: "dashboard", label: "Dashboard", icon: "ri-dashboard-line", element: <AccessDashboard /> },
      { id: "puertas", label: "Puertas y torniquetes", icon: "ri-door-open-line", element: <AccessPointsConsole /> },
      { id: "permisos", label: "Permisos por perfil", icon: "ri-shield-keyhole-line", element: <PermisosSection /> },
      { id: "invitaciones", label: "Invitaciones y QR", icon: "ri-qr-code-line", element: <PassesConsole /> },
      { id: "bitacora", label: "Bitácora de eventos", icon: "ri-file-list-3-line", element: <EventsLog /> },
    ],
  },
  {
    slug: "acceso-vehicular",
    name: "Acceso vehicular",
    description: "Plumas, barreras, vehículos y lectura de placas",
    category: "access-control",
    iconClass: "ri-car-line",
    color: "bg-cyan-500",
    remixIcon: "ri-car-line",
    menu: [
      { id: "dashboard", label: "Dashboard", icon: "ri-dashboard-line", element: <VehicleAccessDashboard /> },
      { id: "plumas", label: "Plumas y barreras", icon: "ri-parking-box-line", element: <PlumasSection /> },
      { id: "vehiculos", label: "Vehículos", icon: "ri-car-line", element: <VehiculosSection /> },
      { id: "placas", label: "Lectura de placas", icon: "ri-camera-lens-line", element: <LecturaPlacasSection /> },
      { id: "registro", label: "Registro vehicular", icon: "ri-history-line", element: <RegistroVehicularSection /> },
    ],
  },
  {
    slug: "estacionamientos",
    name: "Control de estacionamientos",
    description: "Cajones, tickets, cobro, tarifas, pensiones, cortes y facturación",
    category: "access-control",
    iconClass: "ri-parking-box-line",
    color: "bg-sky-600",
    remixIcon: "ri-parking-box-line",
    menu: [
      { id: "tablero", label: "Tablero", icon: "ri-dashboard-line", element: <ParkingDashboard /> },
      { id: "ocupacion", label: "Ocupación", icon: "ri-layout-grid-line", element: <OccupancyScreen /> },
      { id: "tickets", label: "Tickets", icon: "ri-ticket-2-line", element: <TicketsScreen /> },
      { id: "cobro", label: "Caja de cobro", icon: "ri-cash-line", element: <CashierScreen /> },
      { id: "tarifas", label: "Tarifas y validaciones", icon: "ri-price-tag-3-line", element: <RatesScreen /> },
      { id: "pensiones", label: "Pensiones", icon: "ri-vip-crown-line", element: <PensionsScreen /> },
      { id: "facturacion", label: "Facturación", icon: "ri-file-text-line", element: <InvoicingScreen /> },
      { id: "cortes", label: "Cortes de caja", icon: "ri-safe-2-line", element: <ShiftsScreen /> },
      { id: "configuracion", label: "Configuración", icon: "ri-settings-3-line", element: <SettingsScreen /> },
    ],
  },
  {
    slug: "accesos-temporales",
    name: "Accesos temporales",
    description: "QR, PIN y pases digitales de un solo uso con vigencia y zona",
    category: "access-control",
    iconClass: "ri-qr-code-line",
    color: "bg-teal-500",
    remixIcon: "ri-qr-code-line",
    menu: [
      { id: "dashboard", label: "Dashboard", icon: "ri-dashboard-line", element: <PassesDashboard /> },
      { id: "pases", label: "Pases temporales", icon: "ri-qr-code-line", element: <PassesConsole /> },
      { id: "plantillas", label: "Plantillas de invitación", icon: "ri-file-copy-2-line", element: <PlantillasSection /> },
      { id: "autorizaciones", label: "Autorizaciones previas", icon: "ri-checkbox-circle-line", element: <AutorizacionesSection /> },
      { id: "historial", label: "Historial de pases", icon: "ri-history-line", element: <PassesConsole history /> },
    ],
  },
  {
    slug: "reglas-y-auditoria",
    name: "Reglas y auditoría",
    description: "Permisos, listas negras, anti-passback y bitácora de acceso",
    category: "access-control",
    iconClass: "ri-checkbox-multiple-line",
    color: "bg-rose-500",
    remixIcon: "ri-shield-keyhole-line",
    menu: [
      { id: "dashboard", label: "Dashboard", icon: "ri-dashboard-line", element: <ReglasYAuditoriaDashboard /> },
      { id: "reglas", label: "Reglas de acceso", icon: "ri-list-settings-line", element: <ReglasAccesoSection /> },
      { id: "listas-negras", label: "Listas negras", icon: "ri-forbid-line", element: <ListasNegrasSection /> },
      { id: "anti-passback", label: "Anti-passback", icon: "ri-swap-box-line", element: <AntiPassbackSection /> },
      { id: "bitacora", label: "Bitácora de auditoría", icon: "ri-file-list-3-line", element: <AuditoriaSection /> },
      { id: "sospechosos", label: "Intentos sospechosos", icon: "ri-alarm-warning-line", element: <SospechososSection /> },
    ],
  },
  {
    slug: "seguridad-y-vigilancia",
    name: "Seguridad y vigilancia",
    description: "Cámaras, alertas, rondines e incidencias",
    category: "access-control",
    iconClass: "ri-vidicon-line",
    color: "bg-red-500",
    remixIcon: "ri-shield-check-line",
    menu: [
      { id: "dashboard", label: "Dashboard", icon: "ri-dashboard-line", element: <SecurityDashboard /> },
      { id: "camaras", label: "Cámaras", icon: CAMERA_ICON, element: <CameraWall /> },
      { id: "alertas", label: "Alertas", icon: "ri-alarm-warning-line", element: <AlertsConsole /> },
      { id: "rondines", label: "Rondines", icon: "ri-walk-line", element: <RondinesLink /> },
      { id: "reportes", label: "Reportes en campo", icon: "ri-file-warning-line", element: <ReportesCampoSection /> },
      { id: "chat", label: "Chat directo", icon: "ri-chat-3-line", element: <ChatDirectoSection /> },
    ],
  },
  {
    slug: "dispositivos",
    name: "Dispositivos",
    description: "Inventario, estado y mantenimiento del hardware de acceso",
    category: "access-control",
    iconClass: "ri-cpu-line",
    color: "bg-slate-600",
    remixIcon: "ri-cpu-line",
    menu: [
      { id: "dashboard", label: "Dashboard", icon: "ri-dashboard-line", element: <DevicesConsole mode="dashboard" /> },
      { id: "inventario", label: "Inventario de dispositivos", icon: "ri-cpu-line", element: <DevicesConsole /> },
      { id: "controladoras", label: "Controladoras y paneles", icon: "ri-router-line", element: <DevicesConsole mode="controllers" /> },
      { id: "lectores", label: "Lectores y terminales", icon: "ri-fingerprint-line", element: <LectoresSection /> },
      { id: "mantenimiento", label: "Mantenimiento y firmware", icon: "ri-tools-line", element: <MantenimientoSection /> },
    ],
  },
  {
    slug: "gestion-de-comunidad",
    name: "Gestión de comunidad",
    description: "Residentes, unidades, roles, membresías y suscripciones",
    category: "comunity",
    iconClass: "ri-group-line",
    color: "bg-emerald-500",
    remixIcon: "ri-community-line",
    menu: [
      { id: "dashboard", label: "Dashboard", icon: "ri-dashboard-line", element: <ComunidadDashboard /> },
      { id: "directorio", label: "Directorio", icon: "ri-contacts-book-line", element: <DirectorioSection /> },
      { id: "unidades", label: "Unidades y espacios", icon: "ri-home-4-line", element: <UnidadesSection /> },
      { id: "roles", label: "Roles y permisos", icon: "ri-shield-user-line", element: <RolesSection /> },
      { id: "guardias", label: "Guardias y turnos", icon: "ri-user-star-line", element: <GuardiasSection /> },
      { id: "membresias", label: "Membresías", icon: "ri-vip-crown-line", element: <MembresiasSection /> },
      { id: "suscripciones", label: "Suscripciones", icon: "ri-refresh-line", element: <SuscripcionesSection /> },
    ],
  },
  {
    slug: "comunicacion",
    name: "Comunicación y engagement",
    description: "Anuncios, notificaciones, segmentos y canales",
    category: "comunity",
    iconClass: "ri-megaphone-line",
    color: "bg-indigo-500",
    remixIcon: "ri-megaphone-line",
    menu: [
      { id: "dashboard", label: "Dashboard", icon: "ri-dashboard-line", element: <ComunicacionDashboard /> },
      { id: "anuncios", label: "Anuncios y avisos", icon: "ri-megaphone-line", element: <AnunciosSection /> },
      { id: "notificaciones", label: "Notificaciones push", icon: "ri-notification-3-line", element: <NotificacionesSection /> },
      { id: "segmentos", label: "Segmentos", icon: "ri-group-2-line", element: <SegmentosSection /> },
      { id: "canales", label: "Canales", icon: "ri-base-station-line", element: <CanalesSection /> },
    ],
  },
  {
    slug: "pagos",
    name: "Pagos y comercio",
    description: "Cuotas, pagos, punto de venta e ingresos",
    category: "comunity",
    iconClass: "ri-bank-card-line",
    color: "bg-purple-500",
    remixIcon: "ri-bank-card-line",
    menu: [
      { id: "dashboard", label: "Dashboard", icon: "ri-dashboard-line", element: <PagosDashboard /> },
      { id: "cuotas", label: "Cuotas y cobros", icon: "ri-coins-line", element: <CuotasSection /> },
      { id: "pagos", label: "Pagos", icon: "ri-bank-card-line", element: <PagosSection /> },
      { id: "productos", label: "Productos / POS", icon: "ri-store-2-line", element: <ProductosSection /> },
      { id: "ingresos", label: "Reportes de ingresos", icon: "ri-line-chart-line", element: <ReportesIngresosDashboard /> },
    ],
  },
  {
    slug: "reservas",
    name: "Reservas",
    description: "Amenidades, reservaciones, reglas y recordatorios",
    category: "access-control",
    iconClass: "ri-calendar-check-line",
    color: "bg-amber-500",
    remixIcon: "ri-calendar-check-line",
    menu: [
      { id: "dashboard", label: "Dashboard", icon: "ri-dashboard-line", element: <ReservasDashboard /> },
      { id: "amenidades", label: "Amenidades", icon: "ri-community-line", element: <AmenidadesSection /> },
      { id: "reservaciones", label: "Reservaciones", icon: "ri-calendar-check-line", element: <ReservacionesSection /> },
      { id: "reglas", label: "Reglas y aforo", icon: "ri-list-settings-line", element: <ReglasSection /> },
      { id: "recordatorios", label: "Recordatorios", icon: "ri-notification-badge-line", element: <RecordatoriosSection /> },
    ],
  },
];

import { NUVEX_ACCESS_BASE } from "./paths";
export { NUVEX_ACCESS_BASE };

/** Default landing route for the whole section (first functionality dashboard). */
export const nuvexAccessDefaultRoute = `${NUVEX_ACCESS_BASE}/${FUNCTIONALITIES[0].slug}/${FUNCTIONALITIES[0].menu[0].id}`;

/** Route to a functionality's first menu item (used by the apps-sidebar tiles). */
export const functionalityRoute = (f: Functionality) =>
  `${NUVEX_ACCESS_BASE}/${f.slug}/${f.menu[0].id}`;
